import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  ArrowRight,
  CheckCircle2,
  Network,
  Database,
  Layers,
  ShieldCheck,
  Zap,
  Calendar,
  GitMerge,
  Globe,
  FileText,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ROIResultsCapture from '@/components/ROIResultsCapture';

const CALENDLY = 'https://outlook.office.com/bookwithme/user/ca2e84f5d95b4280a34073c39ee722f3@ezcloud.ai/meetingtype/cGjhRG1JmkSrBrj2cFWWjA2?anonymous&ismsaljsauthenabled&ep=mlink';

const EZCLOUD_TO_NETSUITE = [
  { label: 'Vendor bills with line details', note: 'Posted with department, class, location, and project' },
  { label: '3-way match results', note: 'Matched against Purchase Orders and Item Receipts' },
  { label: 'SuiteFlow-ready approval metadata', note: 'Plugs into native NetSuite approval workflows' },
  { label: 'Linked invoice images', note: 'Stored on the Vendor Bill record' },
  { label: 'Tax + subsidiary allocation', note: 'Applied per subsidiary and jurisdiction' },
];

const NETSUITE_TO_EZCLOUD = [
  'Vendor records (active + inactive + banking)',
  'Chart of accounts, departments, classes, locations',
  'Open Purchase Orders + Item Receipts',
  'Subsidiary hierarchy (OneWorld)',
  'Projects / Jobs',
  'Payment terms, tax codes, currency',
];

const DAY_ONE = [
  {
    icon: GitMerge,
    title: 'Vendor bill automation end-to-end',
    body: "Capture, classify, code, route for approval, and post as a Vendor Bill in NetSuite — with PO and Item Receipt references populated for 3-way match.",
  },
  {
    icon: Globe,
    title: 'OneWorld subsidiary routing',
    body: "For multi-subsidiary customers, EZ Cloud reads your full subsidiary tree and routes invoices to the correct subsidiary with intercompany handling built in.",
  },
  {
    icon: Layers,
    title: 'Department, Class, Location, Project coding',
    body: "All four NetSuite segmentation dimensions are available at capture and via auto-coding rules. Approvers see only what they need to.",
  },
  {
    icon: ShieldCheck,
    title: 'Fits your SuiteFlow, not against it',
    body: 'If you already have approval workflows in SuiteFlow, EZ Cloud plugs into them rather than replacing them. We handle everything before posting; your SuiteFlow continues to handle approval routing inside NetSuite when you want it to.',
  },
];

const FAQS = [
  {
    q: 'Does EZ Cloud work with NetSuite OneWorld?',
    a: 'Yes. EZ Cloud reads your full OneWorld subsidiary hierarchy and routes invoices to the correct subsidiary during capture. Intercompany flows are supported out of the box.',
  },
  {
    q: 'Does EZ Cloud replace SuiteFlow for approvals?',
    a: "No — and that's intentional. If your team has already invested in SuiteFlow-based approvals, we keep them. EZ Cloud handles capture, coding, 3-way match, and exception resolution; the invoice posts to NetSuite as a Vendor Bill that enters your SuiteFlow normally. For teams that don't have approval flows built out, EZ Cloud's own approval routing is available instead.",
  },
  {
    q: 'How does EZ Cloud post invoices — via SuiteTalk, RESTlet, or SuiteScript?',
    a: 'REST-based SuiteTalk for supplier, COA, and PO reads. Vendor Bill creation uses the standard NetSuite REST API. No custom SuiteScript deployed on your side.',
  },
  {
    q: 'Can EZ Cloud handle Projects / Jobs for services firms?',
    a: 'Yes. EZ Cloud reads your NetSuite Project hierarchy and routes project-coded invoices to the project manager for review. Common with professional-services and agency customers.',
  },
  {
    q: 'What about non-PO invoices (overheads, utilities, rent)?',
    a: "Fully supported. EZ Cloud routes non-PO invoices through approval workflows based on dollar thresholds, department, or vendor category. The Vendor Bill posts to NetSuite against the correct GL account without needing a PO.",
  },
  {
    q: 'How long does a NetSuite implementation take?',
    a: 'Typical: 8-12 weeks, often toward the shorter end — NetSuite is one of our faster implementations because the SuiteTalk APIs are clean and most customer configuration is already well-structured. Critical-path items are approval matrix mapping and coding rule setup.',
  },
  {
    q: 'Is it SuiteApp-listed?',
    a: 'Contact sales for current SuiteApp status. Deployment does not require marketplace listing — the integration uses standard NetSuite APIs.',
  },
];

const NetSuite = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [roiInputs, setRoiInputs] = useState({ invoices: 2500, costPerInvoice: 16, staffRate: 34 });
  const annualSavings = useMemo(() => {
    const minutesSavedPerInvoice = 11;
    const hoursSaved = (roiInputs.invoices * minutesSavedPerInvoice) / 60;
    const laborMonthly = hoursSaved * roiInputs.staffRate;
    const processingMonthly = roiInputs.invoices * roiInputs.costPerInvoice * 0.5;
    return Math.max(0, (laborMonthly + processingMonthly) * 12);
  }, [roiInputs]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="NetSuite AP Automation | Vendor Bills + OneWorld — EZ Cloud"
        description="Native NetSuite integration for AP automation. Vendor bill posting, OneWorld subsidiary routing, SuiteFlow-aware approvals, project coding. 30-day implementation."
        canonical="https://ezcloud.ai/integrations/netsuite"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Integrations', url: 'https://ezcloud.ai/integrations' },
          { name: 'NetSuite', url: 'https://ezcloud.ai/integrations/netsuite' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      {/* Hero */}
      <section className="bg-hero-gradient text-white pt-16 pb-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="relativecontainer mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
                <Network className="w-4 h-4" aria-hidden />
                Integration · NetSuite
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Vendor bill automation for NetSuite — without fighting your SuiteFlow.
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-8">
                Capture, code, 3-way match, and post Vendor Bills directly into NetSuite. OneWorld-aware. Projects-aware. Approval flows that plug into your existing SuiteFlow instead of replacing it.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
                  <Calendar className="mr-2 w-5 h-5" aria-hidden />
                  See NetSuite integration live
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
                </Button>
                <Button size="lg" variant="outline-on-dark" asChild>
                  <Link to="/technology/native-erp">How our ERP integration works</Link>
                </Button>
              </div>
            </div>

            <Card className="p-6 bg-white/10 border-white/20 backdrop-blur-xl">
              <h2 className="text-white font-semibold mb-4">How the integration works</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-accent/20 rounded-lg border border-accent/30">
                  <div className="font-bold text-white">EZ Cloud</div>
                  <ArrowRight className="w-4 h-4 text-accent mx-auto" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Posts Vendor Bills with segment coding, match results, images</div>
                </div>
                <div className="text-center text-xs text-white/50 py-1">NetSuite REST API (SuiteTalk) · bi-directional</div>
                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="font-bold text-white">NetSuite</div>
                  <ArrowRight className="w-4 h-4 text-gray-600 mx-auto rotate-180" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Returns vendors, COA, subsidiaries, POs + receipts, projects</div>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-4">
                No SuiteScript deployed on your side. No Bundle install required for core flows.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Data flow */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            What flows between EZ Cloud and NetSuite
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Bi-directional, real-time, via NetSuite's REST API.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">EZ Cloud → NetSuite</h3>
              </div>
              <ul className="space-y-3">
                {EZCLOUD_TO_NETSUITE.map(({ label, note }) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" aria-hidden />
                    <div>
                      <div className="text-gray-900 font-medium">{label}</div>
                      <div className="text-sm text-gray-500">{note}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Database className="w-5 h-5 text-primary" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">NetSuite → EZ Cloud</h3>
              </div>
              <ul className="space-y-3">
                {NETSUITE_TO_EZCLOUD.map((label) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="text-gray-900 font-medium">{label}</div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Day-one */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            What you get from day one
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {DAY_ONE.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="p-5 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-gray-600">{body}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Typical customer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Typical EZ Cloud + NetSuite customer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              'Mid-market, NetSuite or NetSuite OneWorld',
              '500–15,000 invoices per month',
              'Multi-subsidiary or multi-location operations',
              'Services firms with Projects / Jobs coding',
              'Existing SuiteFlow approval workflows worth preserving',
              'PE-backed or private-equity-adjacent (finance maturity expected)',
            ].map((x) => (
              <div key={x} className="flex items-start gap-2 text-gray-600">
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                <span>{x}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/industries/manufacturing" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors">
              Manufacturing <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Rough NetSuite ROI</h2>
              <p className="text-gray-600 mb-6">
                Defaults reflect a typical NetSuite mid-market AP team. Tune the inputs to your numbers.
              </p>
              <Button variant="outline" asChild>
                <Link to="/resources/roi-calculator">Full ROI calculator</Link>
              </Button>
            </div>
            <Card className="p-6 bg-accent/10 border-accent/30">
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Monthly invoices</label>
                  <input type="number" min={0} value={roiInputs.invoices}
                    onChange={(e) => setRoiInputs({ ...roiInputs, invoices: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Cost per invoice ($)</label>
                  <input type="number" min={0} value={roiInputs.costPerInvoice}
                    onChange={(e) => setRoiInputs({ ...roiInputs, costPerInvoice: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">AP staff rate ($/hr)</label>
                  <input type="number" min={0} value={roiInputs.staffRate}
                    onChange={(e) => setRoiInputs({ ...roiInputs, staffRate: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
              </div>
              <div className="mb-6">
                <div className="text-sm text-gray-700 mb-1">Estimated annual savings</div>
                <div className="text-4xl font-bold text-accent">${Math.round(annualSavings).toLocaleString()}</div>
              </div>
              <ROIResultsCapture
                industry="NetSuite Integration"
                annualSavings={annualSavings}
                inputs={{
                  'Monthly invoices': roiInputs.invoices.toLocaleString(),
                  'Cost per invoice': `$${roiInputs.costPerInvoice}`,
                  'Staff hourly rate': `$${roiInputs.staffRate}`,
                }}
                buttonLabel="Email my NetSuite ROI report"
              />
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              NetSuite integration FAQ
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-lg px-6 py-2 shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline hover:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to see a Vendor Bill flow live?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            30-minute demo. Bring your sample PDF — we'll capture, code, match, and post it into a NetSuite sandbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
              Book a NetSuite demo
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/technology/native-erp">
                <FileText className="mr-2 w-5 h-5" aria-hidden />
                How native integration works
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NetSuite;
